import React, { useEffect, useState } from 'react';
import './AuthForms.css';
import GainAccessForm from './GainAccessForm';

const passwordRules = [
  { label: 'Password must be at least 8 characters', test: (p) => p.length >= 8 },
  { label: 'Use a number', test: (p) => /\d/.test(p) },
  { label: 'Use a lowercase letter', test: (p) => /[a-z]/.test(p) },
  { label: 'Use an uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { label: 'Use a symbol', test: (p) => /[^a-zA-Z0-9]/.test(p) },
];

const SignupForm = ({ onSuccess, appName, logoUrl, switchToLogin }) => {
  const [step, setStep] = useState('signup');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [toast, setToast] = useState(false);

  const isPasswordValid = passwordRules.every(rule => rule.test(password));
  const isFormValid = name && email && password && isPasswordValid;

  useEffect(() => {
    let timer;
    if (step === 'confirm' && resendTimer > 0) {
      timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendTimer, step]);

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(process.env.REACT_APP_SIGNUP_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name }),
      });

      const data = await res.json();
      if (!res.ok && !data.alreadyExists) throw new Error(data.error);

      if (data.alreadyExists) {
        setStep('gainAccess');
        return;
      }

      setStep('confirm');
      setResendTimer(30);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(process.env.REACT_APP_CONFIRM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      setToast(true);
      setTimeout(() => setToast(false), 10000);
      switchToLogin();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    try {
      const res = await fetch(process.env.REACT_APP_RESEND_CONFIRMATION_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setResendTimer(30);
    } catch (err) {
      setError('Failed to resend code: ' + err.message);
    }
  };

  if (step === 'gainAccess') {
    return <GainAccessForm email={email} name={name} switchToLogin={switchToLogin} />;
  }

  return (
    <div className="auth-wrapper">
      {toast && <div className="auth-success">✅ You've successfully registered! Please login.</div>}
      <form className="auth-form" onSubmit={step === 'signup' ? handleSignup : handleConfirm}>
        {logoUrl && <div className="logo-container"><img src={logoUrl} alt="Logo" /></div>}
        <h2 className="app-title">{appName}</h2>
        <h2>{step === 'signup' ? 'Sign Up' : 'Confirm Your Email'}</h2>
        {error && <div className="auth-error">{error}</div>}

        {step === 'signup' ? (
          <>
            <input
              type="text"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <ul className="password-checklist">
              {passwordRules.map((rule, i) => (
                <li key={i} className={rule.test(password) ? 'valid' : ''}>
                  {rule.test(password) ? '✔' : '✖'} {rule.label}
                </li>
              ))}
            </ul>
            <button type="submit" disabled={!isFormValid || loading}>
              {loading ? 'Signing up...' : 'Sign Up'}
            </button>
            <p className="auth-link">
              Already have an account?{' '}
              <span onClick={switchToLogin}>Log in</span>
            </p>
          </>
        ) : (
          <>
            <input
              type="text"
              placeholder="6-digit code from email"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
            <button type="submit" disabled={loading}>
              {loading ? 'Confirming...' : 'Confirm'}
            </button>
            <button
              type="button"
              disabled={resendTimer > 0 || loading}
              onClick={handleResend}
              style={{ marginTop: 10 }}
            >
              {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Code'}
            </button>
          </>
        )}
      </form>
    </div>
  );
};

export default SignupForm;
